// Engine-driven Kundali renderer. Values come ONLY from /lab/chart (verbatim);
// here we only POSITION them. Fixed anchor + belly tables prevent overlap.

export const PLANET_META = {
  Sun: { ab: "Su", c: "#E05B49" }, Moon: { ab: "Mo", c: "#4FA3E3" },
  Mars: { ab: "Ma", c: "#E8705A" }, Mercury: { ab: "Me", c: "#3FB984" },
  Jupiter: { ab: "Ju", c: "#E8963C" }, Venus: { ab: "Ve", c: "#B06FD6" },
  Saturn: { ab: "Sa", c: "#6E8BB0" }, Rahu: { ab: "Ra", c: "#9AA0A6" },
  Ketu: { ab: "Ke", c: "#C08A6A" },
};
export const PLANET_ORDER = ["Sun", "Moon", "Mars", "Mercury", "Jupiter", "Venus", "Saturn", "Rahu", "Ketu"];
export const LAGNA_C = "#CB8F5A";

// Nakshatra → 3-char abbreviation (English); keeps chart labels compact while
// still showing the nakshatra alongside the degree (e.g. "Ke(R) 04:04 UPh").
const NAK_ABBR = {
  ashwini: "Ash", ashvini: "Ash", bharani: "Bha", krittika: "Kri", kritika: "Kri",
  rohini: "Roh", mrigashira: "Mri", mrigashirsha: "Mri", mrigasira: "Mri", mrigasira1: "Mri",
  ardra: "Ard", arudra: "Ard", punarvasu: "Pun", pushya: "Pus", pushyami: "Pus",
  ashlesha: "Asl", aslesha: "Asl", magha: "Mag", purvaphalguni: "PPh", pubba: "PPh",
  uttaraphalguni: "UPh", uttara: "UPh", hasta: "Has", chitra: "Chi", chitta: "Chi",
  swati: "Swa", swathi: "Swa", vishakha: "Vis", vishaka: "Vis", visakha: "Vis",
  anuradha: "Anu", jyeshtha: "Jye", jyeshta: "Jye", jyestha: "Jye",
  mula: "Mul", moola: "Mul", purvashadha: "PSh", purvaashadha: "PSh", poorvashadha: "PSh",
  uttarashadha: "USh", uttaraashadha: "USh", uttarashada: "USh",
  shravana: "Shr", sravana: "Shr", dhanishta: "Dha", dhanishtha: "Dha", dhanista: "Dha",
  shatabhisha: "Sha", satabhisha: "Sha", shatabhishak: "Sha", shatataraka: "Sha",
  purvabhadrapada: "PBh", poorvabhadrapada: "PBh", uttarabhadrapada: "UBh", revati: "Rev",
};
const nakAbbr = (nak) => {
  if (!nak) return "";
  const k = String(nak).toLowerCase().replace(/[^a-z]/g, "");
  if (NAK_ABBR[k]) return NAK_ABBR[k];
  const hit = Object.keys(NAK_ABBR).find((key) => k.startsWith(key) || key.startsWith(k));
  return hit ? NAK_ABBR[hit] : String(nak).slice(0, 3);
};

const degVal = (dms) => { const m = String(dms || "").match(/^(\d+)°(\d+)/); return m ? +m[1] + (+m[2]) / 60 : 0; };
const degWhole = (dms) => { const m = String(dms || "").match(/^(\d+)°/); return m ? m[1] + "°" : ""; };
const degMin = (dms) => {
  const m = String(dms || "").match(/^(\d+)°(\d+)/);
  return m ? `${(+m[1]).toString().padStart(2, "0")}:${(+m[2]).toString().padStart(2, "0")}` : "";
};

// Split each body into a prominent NAME (coloured) + a smaller DETAIL
// (degree + nakshatra), matching the traditional chart style where the planet
// glyph reads big and the "DD:MM Nak" sits quietly beside it.
const bodyParts = (b) => {
  if (b.isAsc) {
    const m = String(b.dms || "").match(/^(\d+)°(\d+)/);
    return { abbr: "ASC", detail: m ? `${+m[1]}°${m[2].padStart(2, "0")}′` : degWhole(b.dms) };
  }
  const abbr = `${b.ab}${b.retro ? "(R)" : ""}`;
  const detail = [degMin(b.dms), nakAbbr(b.nak)].filter(Boolean).join(" ");
  return { abbr, detail };
};

// Planets (+ Ascendant) stack as neat ROWS inside the house body. The NAME font
// is sized to BOTH the available height (so rows don't collide) and width (so
// long "Ab(R) DD:MM Nak" lines never spill onto the house/sign numbers); the
// detail is ~0.6x the name. A surface-coloured halo keeps the diagonal/grid
// lines from cutting the letters.
function PlanetLines({ bodies, w = 212, h = 86, onPick }) {
  const count = Math.max(bodies.length, 1);
  const parts = bodies.map((b) => ({ ...bodyParts(b), color: b.color, name: b.name, onPick: !!onPick }));
  // approx rendered width of a line for a given name font size `ns`.
  const lineW = (p, ns) => p.abbr.length * ns * 0.62 + (p.detail ? 5 + p.detail.length * (ns * 0.6) * 0.56 : 0);
  let name = Math.min(28, Math.floor(h / count / 1.12));
  while (name > 12 && Math.max(...parts.map((p) => lineW(p, name))) > w) name -= 1;
  name = Math.max(name, 11);
  const det = Math.max(Math.round(name * 0.6), 9);
  const halo = "var(--surface)";
  const shadow = `0 0 3px ${halo}, 0 0 3px ${halo}, 0 0 4px ${halo}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: name >= 16 ? 3 : 1, overflow: "hidden", fontFamily: "'Inter', system-ui, sans-serif", pointerEvents: "none" }}>
      {parts.map((p, i) => (
        <span key={i}
          onClick={onPick ? (e) => { e.stopPropagation(); onPick(p.name); } : undefined}
          style={{ whiteSpace: "nowrap", lineHeight: 1.08, display: "inline-flex", alignItems: "baseline", gap: 4, pointerEvents: onPick ? "auto" : "none", cursor: onPick ? "pointer" : "default" }}>
          <span style={{ fontSize: name, fontWeight: 800, letterSpacing: "-0.01em", color: p.color, textShadow: shadow }}>{p.abbr}</span>
          {p.detail && <span style={{ fontSize: det, fontWeight: 600, color: "var(--ink)", textShadow: shadow }}>{p.detail}</span>}
        </span>
      ))}
    </div>
  );
}

// North: per-house belly box {c:center, n:number anchor, w,h:belly box size}.
// Belly boxes are tuned to sit in the fat part of each bhava and CLEAR the sign
// number (which stays at its traditional inner-vertex spot) so planets and the
// rashi number never overlap. Positions/placements of the houses are unchanged.
const NORTH = {
  1:  { c: [500, 300], n: [500, 438], w: 300, h: 96 },
  2:  { c: [250, 140], n: [250, 212], w: 260, h: 92 },
  3:  { c: [106, 250], n: [190, 250], w: 148, h: 200 },
  4:  { c: [300, 500], n: [438, 500], w: 230, h: 150 },
  5:  { c: [106, 750], n: [190, 750], w: 148, h: 200 },
  6:  { c: [250, 860], n: [250, 788], w: 260, h: 92 },
  7:  { c: [500, 700], n: [500, 562], w: 300, h: 96 },
  8:  { c: [750, 860], n: [750, 788], w: 260, h: 92 },
  9:  { c: [894, 750], n: [810, 750], w: 148, h: 200 },
  10: { c: [700, 500], n: [562, 500], w: 230, h: 150 },
  11: { c: [894, 250], n: [810, 250], w: 148, h: 200 },
  12: { c: [750, 140], n: [750, 212], w: 260, h: 92 },
};
// North: full clickable house polygons (standard North-Indian geometry) so the
// whole bhava — not just the planets — opens the house card on tap.
const NORTH_POLY = {
  1: "500,0 750,250 500,500 250,250", 2: "0,0 500,0 250,250",
  3: "0,0 250,250 0,500", 4: "0,500 250,250 500,500 250,750",
  5: "0,500 250,750 0,1000", 6: "0,1000 250,750 500,1000",
  7: "500,1000 250,750 500,500 750,750", 8: "500,1000 750,750 1000,1000",
  9: "1000,1000 750,750 1000,500", 10: "1000,500 750,750 500,500 750,250",
  11: "1000,500 750,250 1000,0", 12: "1000,0 750,250 500,0",
};
// South: sign(rashi_idx) -> grid cell [col,row]
const SOUTH_CELL = { 0: [1, 0], 1: [2, 0], 2: [3, 0], 3: [3, 1], 4: [3, 2], 5: [3, 3], 6: [2, 3], 7: [1, 3], 8: [0, 3], 9: [0, 2], 10: [0, 1], 11: [0, 0] };

export default function ChartSVG({ chart, variant = "north", varga = "d1", onHouseClick, onPlanetClick }) {
  if (!chart?.planets) return null;
  const d9 = varga === "d9";
  const lagnaIdx = (d9 ? chart.lagna?.d9_rashi_idx : chart.lagna?.rashi_idx) ?? 0;
  const ascDms = d9 ? chart.lagna?.d9_dms : chart.lagna?.dms;
  const asc = { name: "Asc", ab: "ASC", color: LAGNA_C, isAsc: true, house: 1, rashiIdx: lagnaIdx, dms: ascDms, nak: null, retro: false };

  // Normalise bodies
  const bodies = PLANET_ORDER.filter((p) => chart.planets[p]).map((p) => {
    const o = chart.planets[p];
    return {
      name: p, ab: PLANET_META[p].ab, color: PLANET_META[p].c,
      house: d9 ? o.d9_house : o.house,
      rashiIdx: d9 ? o.d9_rashi_idx : o.rashi_idx,
      dms: d9 ? o.d9_dms : o.dms, nak: d9 ? o.d9_nakshatra : o.nakshatra,
      retro: !!o.retrograde,
    };
  });

  const GOLD = "#C9A24B";
  const stroke = "#7a6a38";

  if (variant === "south") {
    const cellBodies = {}; // rashiIdx -> [bodies]
    bodies.forEach((b) => { (cellBodies[b.rashiIdx] = cellBodies[b.rashiIdx] || []).push(b); });
    Object.values(cellBodies).forEach((arr) => arr.sort((a, b) => degVal(a.dms) - degVal(b.dms)));
    // Ascendant sits first in its own rashi cell.
    cellBodies[lagnaIdx] = [asc, ...(cellBodies[lagnaIdx] || [])];
    return (
      <svg viewBox="-16 -16 1032 1032" className="mx-auto block w-full max-w-xl" style={{ fontFamily: "'Inter', system-ui, sans-serif", overflow: "visible" }} role="img" aria-label="South Indian chart">
        <rect x="2" y="2" width="996" height="996" fill="none" stroke={stroke} strokeWidth="4" />
        {/* Outer-ring grid lines only — the centre 2x2 stays ONE merged empty cell */}
        <line x1="250" y1="0" x2="250" y2="1000" stroke={stroke} strokeWidth="2" />
        <line x1="750" y1="0" x2="750" y2="1000" stroke={stroke} strokeWidth="2" />
        <line x1="500" y1="0" x2="500" y2="250" stroke={stroke} strokeWidth="2" />
        <line x1="500" y1="750" x2="500" y2="1000" stroke={stroke} strokeWidth="2" />
        <line x1="0" y1="250" x2="1000" y2="250" stroke={stroke} strokeWidth="2" />
        <line x1="0" y1="750" x2="1000" y2="750" stroke={stroke} strokeWidth="2" />
        <line x1="0" y1="500" x2="250" y2="500" stroke={stroke} strokeWidth="2" />
        <line x1="750" y1="500" x2="1000" y2="500" stroke={stroke} strokeWidth="2" />
        {Object.entries(SOUTH_CELL).map(([ri, [col, row]]) => {
          const x = col * 250, y = row * 250;
          const isLagna = +ri === lagnaIdx;
          const houseNum = (((+ri) - lagnaIdx + 12) % 12) + 1;
          return (
            <g key={ri}>
              <rect x={x} y={y} width="250" height="250" fill="transparent"
                style={{ cursor: onHouseClick ? "pointer" : "default" }}
                onClick={onHouseClick ? () => onHouseClick(houseNum) : undefined}>
                {onHouseClick && <title>{`House ${houseNum}`}</title>}
              </rect>
              {isLagna && <line x1={x + 8} y1={y + 8} x2={x + 70} y2={y + 70} stroke={LAGNA_C} strokeWidth="4" style={{ pointerEvents: "none" }} />}
              <text x={x + 236} y={y + 236} textAnchor="end" fontSize="24" fill={GOLD} fontWeight="700"
                style={{ paintOrder: "stroke", stroke: "var(--surface)", strokeWidth: 7, strokeLinejoin: "round", pointerEvents: "none" }}>{(+ri) + 1}</text>
              <foreignObject x={x + 8} y={y + 16} width="234" height="188" style={{ pointerEvents: "none" }}>
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ height: "100%", color: "var(--ink)", pointerEvents: "none" }}>
                  <PlanetLines bodies={cellBodies[ri] || []} w={234} h={188} onPick={onPlanetClick} />
                </div>
              </foreignObject>
            </g>
          );
        })}
        <text x="500" y="500" textAnchor="middle" dominantBaseline="middle" pointerEvents="none" aria-hidden="true"
          fontSize="26" letterSpacing="1.5" fontWeight="600" fill={GOLD} fillOpacity="0.32"
          style={{ paintOrder: "stroke", stroke: "var(--surface)", strokeWidth: 6, strokeLinejoin: "round" }}>dharmartha.org</text>
      </svg>
    );
  }

  // North
  const houseBodies = {};
  bodies.forEach((b) => { (houseBodies[b.house] = houseBodies[b.house] || []).push(b); });
  Object.values(houseBodies).forEach((arr) => arr.sort((a, b) => degVal(a.dms) - degVal(b.dms)));
  // Ascendant sits first in house 1 (the Lagna).
  houseBodies[1] = [asc, ...(houseBodies[1] || [])];
  return (
    <svg viewBox="-16 -16 1032 1032" className="mx-auto block w-full max-w-xl" style={{ fontFamily: "'Inter', system-ui, sans-serif", overflow: "visible" }} role="img" aria-label="North Indian chart">
      <rect x="2" y="2" width="996" height="996" fill="none" stroke={stroke} strokeWidth="4" />
      <line x1="0" y1="0" x2="1000" y2="1000" stroke={stroke} strokeWidth="3" />
      <line x1="1000" y1="0" x2="0" y2="1000" stroke={stroke} strokeWidth="3" />
      <polygon points="500,0 1000,500 500,1000 0,500" fill="none" stroke={stroke} strokeWidth="3" />
      {Object.entries(NORTH).map(([h, { c, n, w, h: bh }]) => {
        const signNum = ((lagnaIdx + (+h - 1)) % 12) + 1;
        return (
          <g key={h}>
            <polygon points={NORTH_POLY[h]} fill="transparent"
              style={{ cursor: onHouseClick ? "pointer" : "default" }}
              onClick={onHouseClick ? () => onHouseClick(+h) : undefined}>
              {onHouseClick && <title>{`House ${h}`}</title>}
            </polygon>
            <text x={n[0]} y={n[1]} textAnchor="middle" dominantBaseline="middle" fontSize="26" fill={GOLD} fontWeight="700"
              style={{ paintOrder: "stroke", stroke: "var(--surface)", strokeWidth: 8, strokeLinejoin: "round", pointerEvents: "none" }}>{signNum}</text>
            <foreignObject x={c[0] - w / 2} y={c[1] - bh / 2} width={w} height={bh} style={{ pointerEvents: "none" }}>
              <div xmlns="http://www.w3.org/1999/xhtml" style={{ height: "100%", color: "var(--ink)", pointerEvents: "none" }}>
                <PlanetLines bodies={houseBodies[h] || []} w={w} h={bh} onPick={onPlanetClick} />
              </div>
            </foreignObject>
          </g>
        );
      })}
      <text x="500" y="885" textAnchor="middle" dominantBaseline="middle" pointerEvents="none" aria-hidden="true"
        fontSize="19" letterSpacing="0.5" fontWeight="600" fill={GOLD} fillOpacity="0.32"
        style={{ paintOrder: "stroke", stroke: "var(--surface)", strokeWidth: 5, strokeLinejoin: "round" }}>dharmartha.org</text>
    </svg>
  );
}
